import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import Reveal from "@/components/motion/Reveal";
import KineticLines from "@/components/motion/KineticLines";
import { submitContact } from "@/lib/api";
import { Snake, Mushroom, Leaf, Bat, Rose, Candelabra, Ghost } from "@/components/decor/SvgIcons";
import { InstagramLogo, EnvelopeSimple, ArrowRight, Plus, Minus } from "@phosphor-icons/react";

const FAQ = [
  { q: "Can people touch the animals?", a: "Yes — hand-washing before and after, always under our guidance. Our ambassadors are chosen for their calm temperament." },
  { q: "Do you travel?", a: "We cover Ventura and Los Angeles County in person, and offer virtual programs anywhere on Earth (or the moon)." },
  { q: "Are programs customizable?", a: "Absolutely. Themes, species, ages, and length — every event is tailored to your group." },
  { q: "How much space is needed?", a: "A classroom-sized area works for most programs. Wild Wonders needs a bit more room to spread out habitats." },
  { q: "What ages are your programs for?", a: "From two-year-olds to grandparents. We adjust the storytelling to your audience." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [openIdx, setOpenIdx] = useState(0);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitContact(form);
      toast.success("Thank you — a firefly is on its way to us!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("The owls are out — please try again shortly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-28">
      <section className="relative overflow-hidden pb-10">
        <div className="absolute top-10 right-8 opacity-70"><Snake size={100} className="rotate-12" /></div>
        <div className="absolute top-40 left-8 opacity-60"><Mushroom size={60} /></div>
        <div className="absolute top-14 left-1/3 opacity-85 hidden md:block"><Bat size={54} /></div>
        <div className="absolute top-56 right-1/4 opacity-80 hidden md:block"><Ghost size={54} /></div>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="chip mb-6">Say hello</div>
          <KineticLines
            lines={["Whisper into", "the woods."]}
            italicIdx={1}
            className="font-display text-forest text-[13vw] md:text-[7.5vw] lg:text-[6.4rem] leading-[0.96] tracking-tight"
          />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-lg text-forest/80">
              For questions, custom program ideas, media inquiries, or a simple hi from a fellow reptile lover
              — we'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16" data-testid="contact-section">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="grim-card p-8">
              <h3 className="font-display text-3xl text-forest">Find the cottage</h3>
              <ul className="mt-6 space-y-4 text-forest/85">
                <li className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sage/40 flex items-center justify-center"><EnvelopeSimple size={18} weight="duotone" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-forest/60">Email</div>
                    <div>spookyscalessocal@gmail.com</div>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sage/40 flex items-center justify-center"><InstagramLogo size={18} weight="duotone" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-forest/60">Instagram</div>
                    <a
                      href="https://instagram.com/spookyscalessocal"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-underline text-forest hover:text-plum transition-colors"
                      data-testid="contact-instagram-link"
                    >
                      @spookyscalessocal
                    </a>
                  </div>
                </li>
              </ul>

              <div className="swash my-6" />
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-cream border border-sage/50 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-forest/60">Service area</div>
                  <div className="font-display text-forest text-lg mt-1">Ventura &amp; LA County</div>
                </div>
                <div className="rounded-2xl bg-cream border border-sage/50 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-forest/60">Hours</div>
                  <div className="font-display text-forest text-lg mt-1">Sun &amp; Mon · other days by request</div>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <Candelabra size={54} />
                <div className="font-script text-2xl text-plum">tea &amp; snake facts, always brewing.</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="grim-card p-6 md:p-10 space-y-5" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-widest text-forest/70">Name</label>
                  <input required value={form.name} onChange={change("name")} className="input-woodland mt-1" data-testid="contact-name" placeholder="Your name" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-forest/70">Email</label>
                  <input required type="email" value={form.email} onChange={change("email")} className="input-woodland mt-1" data-testid="contact-email" placeholder="you@example.com" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-forest/70">Subject</label>
                  <input value={form.subject} onChange={change("subject")} className="input-woodland mt-1" data-testid="contact-subject" placeholder="Booking, consultation, hello!" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-forest/70">Message</label>
                  <textarea required rows={6} value={form.message} onChange={change("message")} className="input-woodland mt-1" data-testid="contact-message" placeholder="Tell us anything…" />
                </div>
              </div>
              <button type="submit" disabled={sending} className="btn-pill btn-primary w-full justify-center" data-testid="contact-submit">
                {sending ? "Sending…" : "Send message"} <ArrowRight size={20} weight="bold" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20" data-testid="contact-faq">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10">
          <div className="text-center">
            <div className="chip mx-auto">Small questions</div>
            <h2 className="font-display text-5xl md:text-6xl text-forest mt-4 leading-[1.02]">
              Frequently <em className="text-plum">wondered.</em>
            </h2>
          </div>
          <div className="mt-10 space-y-3">
            {FAQ.map((f, i) => (
              <div key={f.q} className="grim-card overflow-hidden">
                <button
                  onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                  className="w-full text-left p-6 flex items-center justify-between gap-4"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display text-forest text-xl md:text-2xl">{f.q}</span>
                  {openIdx === i ? <Minus size={22} /> : <Plus size={22} />}
                </button>
                <AnimatePresence initial={false}>
                  {openIdx === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                      <div className="px-6 pb-6 text-forest/80 leading-relaxed">{f.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
          <div className="mt-14 flex items-center justify-center gap-6 opacity-80">
            <Rose size={44} />
            <Snake size={70} className="rotate-12" />
            <Bat size={44} />
          </div>
        </div>
      </section>
    </div>
  );
}
